import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { ContentState, convertToRaw } from 'draft-js';
import { EventTypes } from 'redux-segment';
import isPlainObject from 'lodash/isPlainObject';
import { WorkspaceType } from 'Shared/constants/types';
import { personalWorkspaceName } from 'Shared/constants/constants';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { getSourcePage } from 'Shared/utils/getSourcePage';

import { imagesFormat, defaultAvatarUrls, cakeAvatarUrls, consumerDomains } from 'Shared/constants/constants';
import { tr } from 'Shared/utils/hooks/use-translation';

// Detects if the client is an in app browser rather than a dedicated web browser
export function userAgentIsInApp(userAgent) {
  const rules = ['WebView', '(iPhone|iPod|iPad)(?!.*Safari/)', 'Android.*(wv|.0.0.0)'];
  const regex = new RegExp(`(${rules.join('|')})`, 'ig');
  return Boolean(userAgent.match(regex));
}

export function formatWorkspaceType(workspace) {
  if (workspace?.isPersonal) return WorkspaceType.isPersonal;
  if (workspace?.isGroup) return WorkspaceType.isGroup;
  if (workspace) return WorkspaceType.isTeammate;
  return null;
}

export function getSurfaceItemTrackingProperties({ surfaceItem, type, workspace, linkClickEvent = false }) {
  //If surface external link was clicked from the team page, don't fetch a workspace type
  const workspace_type = linkClickEvent && getSourcePage() == 'team_page' ? null : formatWorkspaceType(workspace);
  return {
    activity_level: surfaceItem.activity_level,
    integration_type: surfaceItem.type.split('::')[1],
    integration_subtype: surfaceItem.subtype,
    insight_new: surfaceItem.insights.new,
    insight_active: surfaceItem.insights.active,
    insight_blocked: surfaceItem.insights.blocked,
    item_is_private: surfaceItem.private,
    suggestion_score: -surfaceItem.sort_key[0],
    blocked_score: -surfaceItem.sort_key[1],
    activity_score: -surfaceItem.sort_key[2],
    source_page: getSourcePage(),
    item_type: type,
    workspace_type,
  };
}

export function getRandomIconUrl(uuid, type = 'animal') {
  const urls = type === 'animal' ? defaultAvatarUrls : cakeAvatarUrls;
  return urls[uuid.codePointAt(0) % defaultAvatarUrls.length];
}
export function toSentence(arr) {
  return arr.slice(0, -2).join(', ') + (arr.slice(0, -2).length ? ', ' : '') + arr.slice(-2).join(' and ');
}

export function atFromFormValues(formValues) {
  if (formValues.recurrenceInterval === 'day') {
    return formValues.timeOfDay;
  }

  if (formValues.recurrenceInterval === 'week') {
    return formValues.days
      .split(' ')
      .map(d => `${d} ${formValues.timeOfDay}`)
      .join(',');
  }

  return null;
}

export function itemsVisibleUnderTimelineFilter({ items, weeksAgo, groupBy }) {
  const timeline_filter_min = moment().subtract(weeksAgo, 'weeks').startOf('isoWeek').unix();
  const timeline_filter_max = moment().subtract(weeksAgo, 'weeks').endOf('isoWeek').unix();

  if (!items) return [];

  return items.filter(({ completedAt, createdAt }) => {
    const createdAtTimestamp = moment(createdAt).unix();
    switch (groupBy) {
      case 'created_at':
        return createdAtTimestamp >= timeline_filter_min && createdAtTimestamp <= timeline_filter_max;
      case 'completed_at':
        return completedAt && completedAt >= timeline_filter_min && completedAt <= timeline_filter_max;
      default:
        return false;
    }
  });
}

export function recommendationKlassDisplayName(recommendationKlassName) {
  return (
    {
      'Recommendation::SalesforceOpportunityPastDue': tr('perform.helpers.recommendations.missed_close_date'),
      'Recommendation::SalesforceLeadNeedsEmail': tr('perform.helpers.recommendations.uncontacted_lead'),
      'Recommendation::SalesforceContactNeedsCreation': tr('perform.helpers.recommendations.missing_contact_lead'),
      'Recommendation::SalesforceOpportunityNeedsUpdate': tr('perform.helpers.recommendations.no_update_after_meeting'),
      'Recommendation::SalesforceOpportunityNeedsNewActivity': tr(
        'perform.helpers.recommendations.opportunity_no_activity'
      ),
      'Recommendation::SalesforceOpportunityStageNeedsUpdate': tr(
        'perform.helpers.recommendations.opportunity_stuck_in_stage'
      ),
      'Recommendation::SalesforceOpportunityCloseDateNeedsUpdate': tr(
        'perform.helpers.recommendations.dormant_opportunity_upcoming_close'
      ),
      'Recommendation::SalesforceGenericRecordAlert': tr('perform.helpers.recommendations.custom_nudge'),
    }[recommendationKlassName] || recommendationKlassName
  );
}

export async function asyncForEach(array, callback) {
  /* eslint-disable no-await-in-loop */
  for (let index = 0; index < array.length; index += 1) {
    await callback(array[index], index, array);
  }
}

const textareaStyle = Object.freeze({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '2em',
  height: '2em',
  padding: 0,
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  background: 'transparent',
});

export function copyToClipboard(text) {
  const textArea = document.createElement('textarea');

  textArea.style = textareaStyle;
  textArea.value = text;

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  document.execCommand('copy');
  document.body.removeChild(textArea);
}

export function segmentMetaWithPayload({ event, properties }) {
  return {
    analytics: {
      eventType: EventTypes.track,
      eventPayload: { event, properties },
    },
  };
}

export function validEmail(email) {
  const regexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(String(email).toLowerCase());
}

export function isConsumerDomain(email) {
  const userDomain = email.split('@')[1];
  return consumerDomains.some(consumerDomain => userDomain === consumerDomain);
}

export function isValidDomain(domain) {
  const regexp = /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/g;
  return regexp.test(String(domain).toLowerCase());
}

/**
 * Checks if the provided email is included in the allowed domains.
 * @param {{allowedDomains: string[], email: string}} input
 * @returns {boolean}
 */
export function isEmailIncludedInDomains({ allowedDomains, email }) {
  if (!validEmail(email)) return false;
  const domain = email.split('@')[1];
  return allowedDomains
    .map(allowedDomain => allowedDomain.replace('@', '').toLowerCase())
    .includes(domain.toLowerCase());
}

export function defaultPersonalMessage(workspaceName) {
  return tr('perform.helpers.messages.default_personal', workspaceName);
}

export const defaultTeammateMessage = tr('perform.helpers.messages.default_teammate');

// Handles a successful API response.
export function handleSuccess(response) {
  const json = response.data;

  if (json.redirect_path) window.location.href = json.redirect_path;
  else if (json.reload) window.location.reload();
}

// Handles a failed API response. Should be wrapped in a dispatch call.
export function handleError(error) {
  return {
    type: 'RESOURCE_GET_ERROR',
    error:
      (error.response && error.response.data && error.response.data.error) ||
      tr('perform.helpers.errors.something_went_wrong'),
  };
}

export function getAssignmentAttributes({ assignedId, assignmentId }) {
  const assignmentAttributes = [];
  if (assignedId) {
    assignmentAttributes.push({ person_id: assignedId });
  }
  if (assignmentId) {
    assignmentAttributes.push({ id: assignmentId, _destroy: true });
  }
  return assignmentAttributes;
}

export function generateMeetingItemsByDoc({ document, type, parentUuid = null, user = null, workspaceId = null }) {
  if (!document.length) return [];

  const blockDepths = document.map(b => b.depth);
  const minimumDepth = Math.min(...blockDepths);

  let mostRecentTopLevelItem = null;
  return document.map(block => {
    const item = {
      id: new Date().getTime(),
      completed: false,
      attachments: [],
      comments: [],
      history: [],
      position: 1,
      parentId: null,
      parentUuid,
      workspaceId,
      body: JSON.stringify(convertToRaw(ContentState.createFromText(block.text))),
      uuid: uuidv4(),
      item_type: type,
      itemType: type,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      assignmentPersonId: user ? user.person.id : null,
      assignmentAvatar: user ? user.avatar : null,
      assignee: {
        userId: user ? user.id : null,
        personName: user ? user.full_name || user.email : null,
      },
      assigner: {
        userId: user ? user.id : null,
        personName: user ? user.full_name || user.email : null,
      },
    };

    if (block.depth > minimumDepth) {
      item.parentUuid = mostRecentTopLevelItem.uuid;
    } else {
      mostRecentTopLevelItem = item;
    }

    return item;
  });
}

export function getPrepopulatedMeetingItemsForImportFromDoc({ importedDoc }) {
  return importedDoc.length ? generateMeetingItemsByDoc({ document: importedDoc }) : [];
}

export function addCommas(nStr) {
  // eslint-disable-next-line no-param-reassign
  nStr += '';
  const x = nStr.split('.');
  let x1 = x[0];
  let x2;
  if (x.length > 1) {
    x2 = `.${x[1]}`;
  } else {
    x2 = '';
  }
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1,$2');
  }
  return x1 + x2;
}

export function formatPercent(value) {
  if (Number.isNaN(value)) return '0%';
  return `${String(Math.round(value * 100))}%`;
}

export function FormatF(format, str) {
  switch (format) {
    case '$':
      return String(`$${addCommas(str)}`);
    case '%':
      return String(`${Math.round(str)}%`);
    case '%time':
      return tr('perform.helpers.format.percent_of_time', Math.round(str));
    case 'h': {
      const hours = Math.floor(str);
      const minutes = Math.floor((str - hours) * 60.0 + 0.5);
      const minutes_rounded_to_15 = Math.floor((str - hours) * 4.0 + 0.5) * 15.0;

      let hours_displayed = hours > 0 ? hours : null;
      // eslint-disable-next-line no-nested-ternary
      let minutes_displayed = hours > 10 || minutes === 0 ? null : hours > 0 ? minutes_rounded_to_15 : minutes;

      if (minutes_displayed === 60) {
        hours_displayed += 1;
        minutes_displayed = null;
      } else if (minutes_displayed === 0) minutes_displayed = null;

      if (minutes_displayed === null && hours_displayed === null) minutes_displayed = minutes;

      const hours_str =
        // eslint-disable-next-line no-nested-ternary
        hours_displayed != null ? (hours_displayed === 1 ? `${hours_displayed}h` : `${hours_displayed}h`) : '';
      const minutes_str = minutes_displayed != null ? `${minutes_displayed}m` : '';

      const spacer_string = hours_str.length > 0 && minutes_str.length > 0 ? ' ' : '';
      return hours_str + spacer_string + minutes_str;
    }
    case 'hp':
      return tr('perform.helpers.format.hrs_per_employee', str);
    default:
      return str;
  }
}

export function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function modifyFormDataValue(item) {
  if (!item) return '';
  if (typeof item === 'object') return JSON.stringify(item);
  return item;
}

export function getRandomColor() {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

export function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = event => {
      if (event.target.error) {
        reject();
      } else {
        resolve(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  });
}

export function isImageFromContentType(contentType) {
  const format = contentType.split('/').slice(-1)[0];
  return imagesFormat.includes(format);
}

export function addObjectToFormData(formData, data, parentKey) {
  if (typeof data !== 'string' && !data && data !== 0 && data !== !null) return;
  if (isPlainObject(data)) {
    Object.keys(data).forEach(key => {
      addObjectToFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
    return;
  }
  if (data && Array.isArray(data)) {
    data.forEach(item => {
      addObjectToFormData(formData, item, `${parentKey}[]`);
    });
    return;
  }
  formData.append(parentKey, data);
}

export function getInitials(fullName) {
  const initials = fullName.match(/\b\w/g) || [];
  return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
}

export function sanitizeFullName(fullName) {
  if (!fullName) return null;
  const [firstName, lastName] = fullName.split(' ').filter(n => n);
  const lastNameSym = lastName && lastName.charAt(0);
  const sanitized = lastNameSym ? `${firstName} ${lastNameSym}` : firstName;
  return sanitized;
}

export function getFirstName(fullName) {
  const [firtsName] = fullName.split(' ').filter(n => n);
  return firtsName;
}

export function isEmptyString(string) {
  if (!string || typeof string !== 'string') return true;
  return Boolean(!string.replace(/\s/g, '').length);
}

export function isFullName(string) {
  return string.split(' ').filter(n => n).length >= 2;
}

export function capitalize(string) {
  if (typeof string !== 'string') return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function showNotificationError(error) {
  if (error.response?.data?.error) {
    WpToastManager.danger(error.response.data.error);
  } else {
    WpToastManager.danger(tr('perform.helpers.errors.something_went_wrong_exclamation'));
  }
}

export const toggleIntercom = ({ show = true }) => {
  const intercomWrapper = document.getElementById('intercom-container');
  if (intercomWrapper) intercomWrapper.style.setProperty('display', show ? 'block' : 'none', 'important');
};

export const numberToCurrency = (number, options) =>
  number.toLocaleString('en-US', { style: 'currency', currency: 'USD', ...options });

export function greetingTime() {
  const date = new Date();
  const hours = date.getHours();
  if (hours >= 5 && hours < 12) return 'morning';
  if (hours >= 12 && hours < 17) return 'afternoon';
  if (hours >= 17 && hours < 23) return 'evening';
  return 'night';
}

export function getAbbreviation(text, wordLength = 2) {
  const string = text.replace(/\s\s+/g, ' ');
  const res = string
    .split(' ')
    .slice(0, wordLength)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
  return res;
}

export function fullNameFromEmail(email) {
  if (!email) return null;
  if (!validEmail(email)) return null;

  const prefix = email.split('@')[0];

  return prefix
    .split('.')
    .map(w => capitalize(w))
    .join(' ');
}

export function snakeCasetoText(option) {
  return capitalize(option.replace(/_/g, ' '));
}

export function camelToSnakeCase(string) {
  return string.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

export function downloadFile({ file, filename, url }) {
  const element = document.createElement('a');
  element.href = url || URL.createObjectURL(file);
  element.download = filename;
  element.target = '_blank';
  element.rel = 'noopener noreferrer';
  document.body.appendChild(element);
  element.click();
  element.remove();
}

export function getFullNameOrEmail(user) {
  return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email;
}

export function formatWorkspaceTitle({ workspace, user, minimizedVersion = false }) {
  const currentUserFullName = user.full_name;
  if (!workspace) return null;
  const { users, isGroup, name, isPersonal } = workspace;
  if (isPersonal) return personalWorkspaceName;
  if (isGroup) return name;
  const restUser = users.find(({ id: userId }) => userId !== user.id);
  const teammateName = restUser?.fullName || name || restUser?.email;

  const fullNameDecorator = minimizedVersion ? getFirstName : sanitizeFullName;

  return `${fullNameDecorator(teammateName)} <> ${fullNameDecorator(currentUserFullName)}`;
}
